
import React, { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  CreditCard, 
  AlertCircle, 
  CheckCircle2, 
  Loader2, 
  Trash2,
  RefreshCcw
} from 'lucide-react';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { usePaymentMethods, PaymentMethodInfo } from '@/hooks/usePaymentMethods';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCourierInfo } from '@/pages/courier/hooks/useCourierInfo';

export function PaymentMethodCard() {
  const { courierId, loading: courierLoading } = useCourierInfo();
  const { 
    paymentMethod, 
    loading, 
    unlinkPaymentMethod, 
    refreshPaymentMethod 
  } = usePaymentMethods();
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Обработчик отвязки карты
  const handleUnlink = async () => {
    setIsUnlinking(true);
    const success = await unlinkPaymentMethod();
    setIsUnlinking(false);
    
    if (success) {
      setIsDialogOpen(false);
    }
  };

  // Обработчик обновления данных
  const handleRefresh = async () => {
    setIsRefreshing(true);
    refreshPaymentMethod();
    // Устанавливаем таймаут, чтобы состояние обновления отображалось как минимум 500мс
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  };

  // Получение названия платежной системы
  const getCardBrand = (cardType: string | null): string => {
    if (!cardType) return 'Карта';
    
    const brands: {[key: string]: string} = {
      'visa': 'Visa',
      'mastercard': 'MasterCard',
      'maestro': 'Maestro',
      'mir': 'МИР',
      'american_express': 'American Express',
      'jcb': 'JCB',
      'discover': 'Discover',
      'diners_club': 'Diners Club'
    };
    
    return brands[cardType.toLowerCase()] || 'Карта';
  };

  // Форматирование даты обновления
  const formatUpdateDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    try {
      return format(new Date(dateStr), 'd MMMM yyyy в HH:mm', { locale: ru });
    } catch (e) {
      return dateStr;
    }
  };

  // Если идет загрузка данных курьера
  if (courierLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CreditCard className="mr-2 h-5 w-5" /> 
            Платежный метод
          </CardTitle>
          <CardDescription>Загрузка информации о курьере...</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-6">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Если нет ID курьера
  if (!courierId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CreditCard className="mr-2 h-5 w-5" /> 
            Платежный метод
          </CardTitle>
          <CardDescription>Ошибка получения данных</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-6 text-center">
            <div>
              <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
              <p className="text-muted-foreground">Не удалось определить ID курьера</p>
              <p className="text-xs text-muted-foreground mt-1">
                Попробуйте обновить страницу или обратитесь в службу поддержки
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Если идет загрузка данных платежного метода
  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <CreditCard className="mr-2 h-5 w-5" /> 
            Платежный метод
          </CardTitle>
          <CardDescription>Загрузка данных о платежном методе...</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-6">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  // Если платежный метод не привязан
  if (!paymentMethod) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <CreditCard className="mr-2 h-5 w-5" /> 
              Платежный метод
            </CardTitle>
            <CardDescription>У вас нет привязанных карт</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleRefresh} 
            disabled={isRefreshing}
          >
            <RefreshCcw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-6 text-center">
            <div>
              <AlertCircle className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">Для оплаты сервиса необходимо привязать банковскую карту</p>
              <p className="text-xs text-muted-foreground mt-1">
                Карта будет привязана автоматически при первой оплате
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Если есть привязанный платежный метод
  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center">
              <CreditCard className="mr-2 h-5 w-5" /> 
              Платежный метод
            </CardTitle>
            <CardDescription>Информация о привязанной карте</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="icon" 
            onClick={handleRefresh} 
            disabled={isRefreshing}
          >
            <RefreshCcw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="rounded-lg bg-card/50 border p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center">
                  <CheckCircle2 className="h-5 w-5 text-green-500 mr-2" />
                  <span className="font-medium">{getCardBrand(paymentMethod.cardType)}</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  **** {paymentMethod.cardLast4 || '****'}
                </span>
              </div>
              {paymentMethod.expiryMonth && paymentMethod.expiryYear && (
                <div className="text-sm text-muted-foreground">
                  Срок действия: {paymentMethod.expiryMonth}/{paymentMethod.expiryYear}
                </div>
              )}
              {paymentMethod.updatedAt && (
                <div className="text-xs text-muted-foreground mt-1">
                  Привязана: {formatUpdateDate(paymentMethod.updatedAt)}
                </div>
              )}
            </div>
          </div>
        </CardContent>
        <CardFooter>
          <Button 
            variant="destructive" 
            className="w-full" 
            onClick={() => setIsDialogOpen(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Отвязать карту
          </Button>
        </CardFooter>
      </Card>

      <AlertDialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Отвязка карты</AlertDialogTitle>
            <AlertDialogDescription>
              Вы действительно хотите отвязать карту? Это действие нельзя будет отменить. 
              После отвязки карты ваши автоматические платежи перестанут работать.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnlinking}>Отмена</AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => {
                e.preventDefault();
                handleUnlink();
              }}
              disabled={isUnlinking}
              className="bg-destructive hover:bg-destructive/90"
            >
              {isUnlinking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Отвязка...
                </>
              ) : "Отвязать карту"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
